import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { api, fmtEUR } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const years = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 2 + i);
const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];

export default function Parametres() {
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [obj, setObj] = useState(null);
  const [settings, setSettings] = useState(null);

  const load = async (y) => {
    const [o, s] = await Promise.all([
      api.get(`/objectifs/${y}`),
      api.get('/settings'),
    ]);
    setObj({
      ...o.data,
      ca_mensuel_estime: o.data.ca_mensuel_estime?.length === 12 ? o.data.ca_mensuel_estime : Array(12).fill(0),
      charges_mensuelles_estimees: o.data.charges_mensuelles_estimees?.length === 12 ? o.data.charges_mensuelles_estimees : Array(12).fill(0),
    });
    setSettings(s.data);
  };

  useEffect(() => { load(annee); }, [annee]);

  const setGlobal = (key, val) => setObj({ ...obj, [key]: Number(val) });
  const setMois = (key, idx, val) => {
    const arr = [...obj[key]];
    arr[idx] = Number(val);
    setObj({ ...obj, [key]: arr });
  };

  const distribute = (key, total) => {
    const per = Number(total) / 12;
    setObj({ ...obj, [key === 'ca' ? 'ca_estime' : 'charges_estimees']: Number(total), [key === 'ca' ? 'ca_mensuel_estime' : 'charges_mensuelles_estimees']: Array(12).fill(per) });
  };

  const save = async () => {
    try {
      await api.post('/objectifs', {
        annee,
        ca_estime: Number(obj.ca_estime),
        charges_estimees: Number(obj.charges_estimees),
        fonds_roulement_initial: Number(obj.fonds_roulement_initial),
        ca_mensuel_estime: obj.ca_mensuel_estime.map(Number),
        charges_mensuelles_estimees: obj.charges_mensuelles_estimees.map(Number),
      });
      if (settings) {
        await api.put('/settings', settings);
      }
      toast.success('Paramètres sauvegardés');
      load(annee);
    } catch (err) {
      toast.error('Erreur de sauvegarde');
    }
  };

  if (!obj) return <Layout title="Paramètres"><div>Chargement...</div></Layout>;

  const totalCA = obj.ca_mensuel_estime.reduce((s, v) => s + Number(v || 0), 0);
  const totalCharges = obj.charges_mensuelles_estimees.reduce((s, v) => s + Number(v || 0), 0);

  return (
    <Layout
      title="Paramètres"
      subtitle="Objectifs annuels & répartition"
      actions={
        <div className="flex gap-2">
          <Select value={String(annee)} onValueChange={(v) => setAnnee(Number(v))}>
            <SelectTrigger className="w-32 rounded-none border-zinc-900" data-testid="params-annee-select"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={save} className="rounded-none bg-[#002FA7] hover:bg-[#001B63]" data-testid="save-params-btn">Sauvegarder</Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="bg-white border border-zinc-200 border-l-4 border-l-[#002FA7] p-6">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 block">CA annuel estimé</Label>
          <Input type="number" className="rounded-none font-mono text-2xl h-14" value={obj.ca_estime} onChange={(e) => setGlobal('ca_estime', e.target.value)} data-testid="ca-estime-input" />
          <button onClick={() => distribute('ca', obj.ca_estime)} className="text-xs text-[#002FA7] hover:underline mt-2">Répartir /12 →</button>
        </div>
        <div className="bg-white border border-zinc-200 p-6">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 block">Charges annuelles estimées</Label>
          <Input type="number" className="rounded-none font-mono text-2xl h-14" value={obj.charges_estimees} onChange={(e) => setGlobal('charges_estimees', e.target.value)} data-testid="charges-estime-input" />
          <button onClick={() => distribute('charges', obj.charges_estimees)} className="text-xs text-[#002FA7] hover:underline mt-2">Répartir /12 →</button>
        </div>
        <div className="bg-white border border-zinc-200 p-6">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 block">Fonds de roulement initial</Label>
          <Input type="number" className="rounded-none font-mono text-2xl h-14" value={obj.fonds_roulement_initial} onChange={(e) => setGlobal('fonds_roulement_initial', e.target.value)} data-testid="fonds-initial-input" />
        </div>
      </div>

      <div className="bg-white border border-zinc-200 p-6 mb-6">
        <h3 className="font-display font-bold text-xl tracking-tight mb-4">Répartition mensuelle</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-zinc-900">
              <tr>
                <th className="px-2 py-2 text-left text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Poste</th>
                {months.map((m) => (
                  <th key={m} className="px-2 py-2 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium text-center">{m}</th>
                ))}
                <th className="px-2 py-2 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-zinc-100">
                <td className="px-2 py-2 font-medium">CA estimé</td>
                {obj.ca_mensuel_estime.map((v, i) => (
                  <td key={i} className="px-1 py-1">
                    <Input type="number" className="rounded-none font-mono text-xs h-9 px-2" value={v} onChange={(e) => setMois('ca_mensuel_estime', i, e.target.value)} />
                  </td>
                ))}
                <td className="px-2 py-2 font-mono text-right font-medium">{fmtEUR(totalCA)}</td>
              </tr>
              <tr>
                <td className="px-2 py-2 font-medium">Charges estimées</td>
                {obj.charges_mensuelles_estimees.map((v, i) => (
                  <td key={i} className="px-1 py-1">
                    <Input type="number" className="rounded-none font-mono text-xs h-9 px-2" value={v} onChange={(e) => setMois('charges_mensuelles_estimees', i, e.target.value)} />
                  </td>
                ))}
                <td className="px-2 py-2 font-mono text-right font-medium">{fmtEUR(totalCharges)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 border-l-4 border-l-violet-500 p-6 mt-6" data-testid="settings-remu-dirig">
        <h3 className="font-display font-bold text-xl tracking-tight mb-1">Rémunération des dirigeants</h3>
        <p className="text-xs text-zinc-500 mb-4">
          Valeurs par défaut proposées sur les nouveaux projets. Modifiables projet par projet ensuite.
        </p>
        {settings && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 block">Mode par défaut</Label>
              <Select value={settings.remu_dirig_mode_defaut || 'pct_ca_apres_assurance'} onValueChange={(v) => setSettings({ ...settings, remu_dirig_mode_defaut: v })}>
                <SelectTrigger className="rounded-none" data-testid="settings-remu-mode"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="aucun">Aucune</SelectItem>
                  <SelectItem value="pct_ca">% du CA (Honoraires × taux)</SelectItem>
                  <SelectItem value="pct_ca_apres_assurance">% du CA après assurance</SelectItem>
                  <SelectItem value="manuel">Saisie manuelle</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 block">Taux par défaut (%)</Label>
              <Input type="number" step="0.5" className="rounded-none font-mono"
                value={(Number(settings.remu_dirig_taux_defaut) || 0) * 100}
                onChange={(e) => setSettings({ ...settings, remu_dirig_taux_defaut: (Number(e.target.value) || 0) / 100 })}
                data-testid="settings-remu-taux" />
              <div className="text-[10px] text-zinc-500 mt-1">
                Recommandé : 30% du CA après assurance ou 20% du CA brut
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-zinc-100 border border-zinc-300 p-6 mt-6">
        <h4 className="font-display font-bold text-sm uppercase tracking-wider mb-3">Constantes métier</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Taux honoraires</div>
            <div className="font-mono font-medium">0,85% — 2,00%</div>
            <div className="text-xs text-zinc-600 mt-1">du montant des travaux</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Taux assurance</div>
            <div className="font-mono font-medium">0,178%</div>
            <div className="text-xs text-zinc-600 mt-1">du montant des travaux</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Étalement assurance</div>
            <div className="font-mono font-medium">jusqu'à 4 ans</div>
            <div className="text-xs text-zinc-600 mt-1">durée des travaux</div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
